/**
 * Structured sections of a job posting — the parts a real EMS listing has beyond a one-paragraph
 * summary: key responsibilities, required / preferred qualifications, shift details, and
 * compensation notes. One definition of their size limits and clean-up rules, used by the Post a
 * Job form, the candidate job page, and the AI draft Cloud Function.
 *
 * Same contract as descriptionLength.ts: AI text is composed to a budget and then run through
 * fitTextToLimit(), so nothing is ever cut mid-word or mid-sentence on its way into a field.
 *
 * Shared with functions/ via scripts/syncFunctionsGenerated.mjs (functions/src/generated/lib/jobs/).
 */
import { JOB_SUMMARY_MAX_CHARS, fitTextToLimit } from './descriptionLength';

export type JobListSectionKey =
  | 'keyResponsibilities'
  | 'requiredQualifications'
  | 'preferredQualifications'
  /** "What We Offer" — the employer's own benefit lines, beside the preset benefit chips. */
  | 'whatWeOffer'
  /** "Physical requirements" (2026-10-09), e.g. "Must be able to lift and carry 125 pounds". */
  | 'physicalRequirements'
  /** "Minimum qualifications" (2026-10-09): the employer's own must-haves, e.g. "18 years or older". */
  | 'minimumQualifications';
export type JobTextSectionKey =
  | 'scheduleDetails'
  | 'compensationDetails'
  /** "Additional Information" — the catch-all: HIPAA / confidentiality notes, hours vary, paid or unpaid. */
  | 'additionalInformation';

export const JOB_LIST_SECTION_LIMITS: Record<JobListSectionKey, { maxItems: number; itemMaxChars: number }> = {
  // 25 (was 10, 2026-10-05): real EMS postings run past ten duties. The AI draft still writes 5 to 8.
  keyResponsibilities: { maxItems: 25, itemMaxChars: 200 },
  requiredQualifications: { maxItems: 10, itemMaxChars: 200 },
  preferredQualifications: { maxItems: 8, itemMaxChars: 200 },
  whatWeOffer: { maxItems: 12, itemMaxChars: 200 },
  physicalRequirements: { maxItems: 12, itemMaxChars: 200 },
  minimumQualifications: { maxItems: 15, itemMaxChars: 200 },
};

export const JOB_TEXT_SECTION_LIMITS: Record<JobTextSectionKey, { maxChars: number; targetChars: number }> = {
  scheduleDetails: { maxChars: 500, targetChars: 400 },
  compensationDetails: { maxChars: 500, targetChars: 400 },
  additionalInformation: { maxChars: 1500, targetChars: 1200 },
};

/**
 * What the AI draft returns: one entry per DRAFTED section, empty when the model had no facts to
 * work from. The two qualification sections are deliberately absent — requirements are facts the
 * employer states (selections, hiring preferences, their own lines), never something a model writes.
 */
export type JobPostingDraftSections = {
  summary: string;
  keyResponsibilities: string[];
  scheduleDetails: string;
  compensationDetails: string;
};

/** "- Foo", "• Foo", "* Foo", "1. Foo", "2) Foo" → "Foo". */
function stripListMarker(line: string): string {
  return line.replace(/^\s*(?:[-–—•*·▪◦]+|\d{1,2}[.)])\s+/, '').trim();
}

/** One list item per non-empty line; list markers removed. Used for pasted text and legacy string fields. */
export function splitLinesToList(text: unknown): string[] {
  if (typeof text !== 'string') return [];
  return text
    .split(/\r?\n/)
    .map(stripListMarker)
    .filter((line) => line.length > 0);
}

/**
 * Clean a list section from any source (form state, Firestore, AI reply): strings only, markers and
 * blanks removed, case-insensitive duplicates dropped, each item fitted to its limit at a sentence
 * or word boundary, and the list capped.
 */
export function normalizeJobList(value: unknown, key: JobListSectionKey): string[] {
  const { maxItems, itemMaxChars } = JOB_LIST_SECTION_LIMITS[key];
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? splitLinesToList(value) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const item = fitTextToLimit(stripListMarker(entry), itemMaxChars);
    if (!item) continue;
    const dedupeKey = item.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    out.push(item);
    if (out.length >= maxItems) break;
  }
  return out;
}

export function normalizeJobText(value: unknown, key: JobTextSectionKey): string {
  if (typeof value !== 'string') return '';
  return fitTextToLimit(value, JOB_TEXT_SECTION_LIMITS[key].maxChars);
}

/** Coerce an untrusted AI reply (parsed JSON) into clean, in-budget sections. */
export function normalizeDraftSections(raw: unknown): JobPostingDraftSections {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    summary: typeof o.summary === 'string' ? fitTextToLimit(o.summary, JOB_SUMMARY_MAX_CHARS) : '',
    keyResponsibilities: normalizeJobList(o.keyResponsibilities, 'keyResponsibilities'),
    scheduleDetails: normalizeJobText(o.scheduleDetails, 'scheduleDetails'),
    compensationDetails: normalizeJobText(o.compensationDetails, 'compensationDetails'),
  };
}
